import { useState } from "react";
import { Sparkles, Wand2, Feather, Baby, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { streamPost } from "@/lib/stream";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// AI helper for one editor field: draft from a brief, polish, or simplify.
export const AiWriteButton = ({ field, value, onChange, context, testId }) => {
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [prompt, setPrompt] = useState("");

  const run = async (action, brief = "") => {
    setBusy(true);
    const previous = value;
    try {
      await streamPost("/admin/ai/write", { action, field, text: value || "", prompt: brief, ...context }, { onDelta: (_, full) => onChange(full) });
      toast.success(action === "draft" ? "Draft ready — edit freely" : action === "polish" ? "Text polished" : "Text simplified", {
        action: { label: "Undo", onClick: () => onChange(previous) },
      });
      setDrafting(false); setPrompt("");
    } catch (e) {
      onChange(previous);
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col items-end gap-2" data-testid={testId}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" size="sm" variant="outline" disabled={busy} className="rounded-full h-8 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid={`${testId}-trigger`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {busy ? "Writing…" : "AI help"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => setDrafting(true)} data-testid={`${testId}-draft`}><Wand2 className="w-4 h-4 mr-2" />Draft from a brief</DropdownMenuItem>
          <DropdownMenuItem disabled={!value?.trim()} onClick={() => run("polish")} data-testid={`${testId}-polish`}><Feather className="w-4 h-4 mr-2" />Polish & tighten</DropdownMenuItem>
          <DropdownMenuItem disabled={!value?.trim()} onClick={() => run("simplify")} data-testid={`${testId}-simplify`}><Baby className="w-4 h-4 mr-2" />Simplify language</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {drafting && (
        <form onSubmit={(e) => { e.preventDefault(); run("draft", prompt); }} className="flex gap-2 w-full sm:w-[420px]" data-testid={`${testId}-draft-form`}>
          <Input autoFocus value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={1000} placeholder="Brief, e.g. explain why a longer exhale calms the body" className="h-9 rounded-xl text-sm bg-white" data-testid={`${testId}-draft-input`} />
          <Button type="submit" size="sm" disabled={busy || !prompt.trim()} className="rounded-xl h-9 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`${testId}-draft-submit`}>Draft</Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setDrafting(false)} className="rounded-xl h-9">Cancel</Button>
        </form>
      )}
    </div>
  );
};
